import { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import {
  ArrowLeft,
  ArrowRight,
  BookOpenCheck,
  CheckCircle2,
  GraduationCap,
  HandHeart,
  HeartPulse,
  MapPin,
  Monitor,
  ShieldCheck,
  Sparkles,
  Users,
} from "lucide-react";
import { useRegionalExperience } from "../hooks/useRegionalExperience";

const iconMap = {
  HeartPulse,
  MonitorCog: Monitor,
  Monitor,
  GraduationCap,
  ShieldPlus: ShieldCheck,
  ShieldCheck,
  HandHeart,
  BookOpenCheck,
};

const galleryClasses = [
  "md:col-span-7 md:row-span-2",
  "md:col-span-5",
  "md:col-span-5",
  "md:col-span-4",
  "md:col-span-4",
  "md:col-span-4",
];

function resolveInternalHref(href, fallback = "/donate") {
  if (!href) return fallback;
  if (href.startsWith("#")) return "/vedant";
  return href;
}

const ActionLink = ({ to, className, children }) => {
  if (to?.startsWith("http")) {
    return (
      <a href={to} target="_blank" rel="noopener noreferrer" className={className}>
        {children}
      </a>
    );
  }

  return (
    <Link to={to} className={className}>
      {children}
    </Link>
  );
};

const SectionHeading = ({ eyebrow, title, text }) => (
  <div className="max-w-3xl">
    <p className="mb-3 text-xs font-semibold uppercase tracking-[0.28em] text-[#9d1f1c]">
      {eyebrow}
    </p>
    <h2 className="text-3xl font-semibold leading-tight text-[#14201f] md:text-5xl">{title}</h2>
    {text && <p className="mt-5 text-base leading-8 text-[#52615f] md:text-lg">{text}</p>}
  </div>
);

const ProgramDetail = () => {
  const { programId } = useParams();
  const { siteConfig, region } = useRegionalExperience();

  const programs = siteConfig.programPillars || [];
  const program = programs.find((item) => item.id === programId);
  const detail = (siteConfig.programDetails || []).find((item) => item.id === programId);
  const merged = program && detail ? { ...program, ...detail } : program;
  const Icon = iconMap[program?.icon] || Sparkles;
  const donationHref = resolveInternalHref(program?.ctaHref, region.donationPath);
  const gallery = merged?.gallery?.length
    ? merged.gallery
    : [{ src: merged?.image, srcMobile: merged?.imageMobile, alt: merged?.title, caption: merged?.description }].filter((item) => item.src);
  const otherPrograms = programs.filter((item) => item.id !== programId).slice(0, 3);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [programId]);

  if (!merged) {
    return (
      <main className="min-h-screen bg-[#fffaf5] px-4 py-32 text-[#14201f]">
        <div className="container mx-auto max-w-3xl rounded-lg border border-[#ead9cd] bg-white p-8">
          <p className="text-xs font-semibold uppercase tracking-[0.28em] text-[#9d1f1c]">
            Program not found
          </p>
          <h1 className="mt-4 text-4xl font-semibold">This AGM program page is not available yet.</h1>
          <Link
            to="/programs"
            className="mt-7 inline-flex items-center gap-2 rounded-md bg-[#9d1f1c] px-5 py-3 text-sm font-bold uppercase tracking-[0.14em] text-white"
          >
            Back to programs
            <ArrowRight size={18} />
          </Link>
        </div>
      </main>
    );
  }

  return (
    <main className="overflow-x-hidden bg-[#fffaf5] text-[#14201f]">
      <section className="relative overflow-hidden bg-[#2a120d] pt-28 text-white">
        <picture>
          {merged.heroImageMobile || merged.imageMobile ? (
            <source
              srcSet={merged.heroImageMobile || merged.imageMobile}
              media="(max-width: 767px)"
            />
          ) : null}
          <img
            src={merged.heroImage || merged.image}
            alt={merged.title}
            className="absolute inset-0 h-full w-full object-cover opacity-60"
            style={{ objectPosition: merged.heroPosition || "center" }}
          />
        </picture>
        <div className="absolute inset-0 bg-gradient-to-r from-[#2a120d] via-[#35170f]/82 to-[#4b2418]/20" />
        <div className="container relative z-10 mx-auto px-4 py-20 sm:px-6 lg:px-8 lg:py-28">
          <Link
            to="/programs"
            className="mb-8 inline-flex items-center gap-2 text-sm font-semibold text-white/78 transition hover:text-white"
          >
            <ArrowLeft size={17} />
            Programs
          </Link>

          <div className="max-w-5xl">
            <div className="mb-5 inline-flex items-center gap-3 rounded-full border border-white/20 bg-white/10 px-4 py-2 backdrop-blur">
              <Icon className="h-4 w-4 text-[#f5c45e]" />
              <span className="text-xs font-bold uppercase tracking-[0.22em] text-[#f5c45e]">
                {merged.eyebrow}
              </span>
            </div>
            <h1 className="max-w-4xl text-3xl font-semibold leading-tight md:text-7xl">{merged.title}</h1>
            <p className="mt-7 max-w-3xl text-lg leading-8 text-white/84 md:text-xl">{merged.deck || merged.description}</p>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <ActionLink
                to={donationHref}
                className="inline-flex items-center justify-center gap-3 rounded-md bg-[#f5c45e] px-6 py-4 text-sm font-bold uppercase tracking-[0.14em] text-[#201711] transition hover:bg-[#ffd77c]"
              >
                {program.ctaLabel || region.paymentLabel}
                <ArrowRight size={18} />
              </ActionLink>
              <Link
                to="/volunteer"
                className="inline-flex items-center justify-center gap-3 rounded-md border border-white/35 px-6 py-4 text-sm font-bold uppercase tracking-[0.14em] text-white transition hover:bg-white hover:text-[#4b2418]"
              >
                Volunteer
                <Users size={18} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="py-14 md:py-16">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid gap-4 md:grid-cols-4">
            {(merged.stats || []).map((stat) => (
              <article key={`${stat.value}-${stat.label}`} className="rounded-lg border border-[#ead9cd] bg-white p-6 shadow-sm">
                <p className="text-3xl font-semibold text-[#9d1f1c]">{stat.value}</p>
                <h2 className="mt-2 text-sm font-bold uppercase tracking-[0.16em] text-[#4b2418]">{stat.label}</h2>
                <p className="mt-3 text-sm leading-6 text-[#6f5a4e]">{stat.note}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="pb-20 md:pb-28">
        <div className="container mx-auto grid gap-10 px-4 sm:px-6 lg:grid-cols-[0.82fr_1.18fr] lg:px-8">
          <div>
            <div className="sticky top-28 rounded-lg border border-[#ead9cd] bg-white p-7 shadow-sm">
              <p className="text-xs font-semibold uppercase tracking-[0.24em] text-[#9d1f1c]">
                Field context
              </p>
              <p className="mt-5 text-lg leading-8 text-[#4f3d34]">{merged.lead || merged.description}</p>
              {merged.location && (
                <p className="mt-6 flex gap-3 text-sm font-semibold text-[#4b2418]">
                  <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-[#9d1f1c]" />
                  {merged.location}
                </p>
              )}
            </div>
          </div>

          <div className="grid gap-6">
            {(merged.sections || []).map((section) => (
              <article key={section.title} className="rounded-lg border border-[#ead9cd] bg-white p-7 shadow-sm md:p-9">
                <p className="text-xs font-semibold uppercase tracking-[0.24em] text-[#9d1f1c]">
                  {section.eyebrow}
                </p>
                <h2 className="mt-4 text-2xl font-semibold leading-tight text-[#14201f] md:text-4xl">
                  {section.title}
                </h2>
                <p className="mt-5 text-base leading-8 text-[#52615f]">{section.body}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-white py-20 md:py-28">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading
            eyebrow="Program model"
            title="What this program does on the ground."
            text="A strong NGO program should make the work visible: what is delivered, who benefits and how support strengthens the system."
          />

          <div className="mt-12 grid gap-6 lg:grid-cols-2">
            <article className="rounded-lg border border-[#ead9cd] bg-[#fffaf5] p-7 md:p-9">
              <h3 className="text-2xl font-semibold text-[#14201f]">Service pathways</h3>
              <div className="mt-7 grid gap-4">
                {(merged.pathways || []).map((item) => (
                  <div key={item} className="flex gap-3 text-sm leading-7 text-[#4f3d34]">
                    <CheckCircle2 className="mt-1 h-4 w-4 shrink-0 text-[#9d1f1c]" />
                    <span>{item}</span>
                  </div>
                ))}
              </div>
            </article>

            <article className="rounded-lg border border-[#ead9cd] bg-[#fffaf5] p-7 md:p-9">
              <h3 className="text-2xl font-semibold text-[#14201f]">How donors strengthen it</h3>
              <div className="mt-7 grid gap-4">
                {(merged.donorUses || []).map((item) => (
                  <div key={item} className="flex gap-3 text-sm leading-7 text-[#4f3d34]">
                    <CheckCircle2 className="mt-1 h-4 w-4 shrink-0 text-[#9d1f1c]" />
                    <span>{item}</span>
                  </div>
                ))}
              </div>
            </article>
          </div>
        </div>
      </section>

      <section id="gallery" className="py-20 md:py-28">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col gap-8 lg:flex-row lg:items-end lg:justify-between">
            <SectionHeading
              eyebrow="Evidence gallery"
              title="A program should be seen, not merely described."
              text="Photographs help supporters understand the human setting, team presence and field reality behind the program."
            />
            <ActionLink
              to={donationHref}
              className="inline-flex w-fit items-center gap-3 rounded-md bg-[#4b2418] px-5 py-3 text-sm font-bold uppercase tracking-[0.14em] text-white transition hover:bg-[#7f1916]"
            >
              {program.ctaLabel || region.paymentLabel}
              <ArrowRight size={18} />
            </ActionLink>
          </div>

          <div className="mt-12 grid auto-rows-fr gap-5 md:grid-cols-12">
            {gallery.map((image, index) => (
              <figure
                key={`${image.src}-${index}`}
                className={`group overflow-hidden rounded-lg border border-[#ead9cd] bg-white shadow-sm ${
                  galleryClasses[index] || "md:col-span-4"
                }`}
              >
                <div className={`${index === 0 ? "aspect-[16/11] md:h-full" : "aspect-[4/3]"} overflow-hidden`}>
                  <picture>
                    {image.srcMobile ? (
                      <source srcSet={image.srcMobile} media="(max-width: 767px)" />
                    ) : null}
                    <img
                      src={image.src}
                      alt={image.alt || merged.title}
                      className="h-full w-full object-cover transition duration-700 group-hover:scale-105"
                      loading="lazy"
                    />
                  </picture>
                </div>
                {image.caption && (
                  <figcaption className="p-4 text-sm font-medium leading-6 text-[#4f3d34]">
                    {image.caption}
                  </figcaption>
                )}
              </figure>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-[linear-gradient(135deg,#2a120d_0%,#4b2418_56%,#7f1916_100%)] py-16 text-white">
        <div className="container mx-auto grid gap-8 px-4 sm:px-6 lg:grid-cols-[1fr_0.72fr] lg:items-center lg:px-8">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.28em] text-[#f5c45e]">
              Make this stronger
            </p>
            <h2 className="mt-4 max-w-3xl text-3xl font-semibold leading-tight md:text-5xl">
              Help AGM expand this work with clarity, dignity and field proof.
            </h2>
            <p className="mt-5 max-w-2xl text-sm leading-7 text-white/76">
              Supporters in {region.label} can give through the regional donation pathway and help this program grow with better systems, stronger documentation and deeper service.
            </p>
          </div>
          <div className="flex flex-col gap-3 sm:flex-row lg:flex-col">
            <ActionLink
              to={donationHref}
              className="inline-flex items-center justify-center gap-3 rounded-md bg-[#f5c45e] px-6 py-4 text-sm font-bold uppercase tracking-[0.14em] text-[#201711] transition hover:bg-[#ffd77c]"
            >
              {program.ctaLabel || region.paymentLabel}
              <ArrowRight size={18} />
            </ActionLink>
            <Link
              to="/programs"
              className="inline-flex items-center justify-center gap-3 rounded-md border border-white/25 px-6 py-4 text-sm font-bold uppercase tracking-[0.14em] text-white transition hover:bg-white hover:text-[#4b2418]"
            >
              Explore all programs
              <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>

      {otherPrograms.length > 0 && (
        <section className="py-16 md:py-20">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <SectionHeading eyebrow="Keep exploring" title="Other AGM program pages." />
            <div className="mt-10 grid gap-5 md:grid-cols-3">
              {otherPrograms.map((item) => (
                <Link
                  key={item.id}
                  to={`/programs/${item.id}`}
                  className="group overflow-hidden rounded-lg border border-[#ead9cd] bg-white shadow-sm transition hover:-translate-y-1 hover:shadow-xl"
                >
                  <div className="aspect-[16/10] overflow-hidden">
                    <img
                      src={item.image}
                      alt={item.title}
                      className="h-full w-full object-cover transition duration-700 group-hover:scale-105"
                      loading="lazy"
                    />
                  </div>
                  <div className="p-5">
                    <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#9d1f1c]">
                      {item.eyebrow}
                    </p>
                    <h3 className="mt-2 text-xl font-semibold leading-tight text-[#14201f]">{item.title}</h3>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </main>
  );
};

export default ProgramDetail;
